<script lang="ts">
  import { onMount } from 'svelte';
  import { page } from '$app/stores';
  import { goto } from '$app/navigation';
  import { Calendar, ChevronLeft, Eye, MessageCircle, Share2, Heart } from 'lucide-svelte';
  import { authStore } from '$lib/stores/authStore';
  import { supabase } from '$lib/supabase';
  import ShareButton from '$lib/components/ShareButton.svelte';
  import Chart from 'chart.js/auto';
  
  export let data;
  
  const { article, featuredVideos } = data;
  let comments: CommentNode[] = data.comments || [];
  let viewerChart: Chart | null = null;
  let chartCanvas: HTMLCanvasElement;
  
  let newComment = '';
  let replyingTo: string | null = null;
  let replyContent = '';
  let isSubmitting = false;
  let errorMessage = '';
  let successMessage = '';
  
  interface BaseComment {
    id: string;
    content: string;
    created_at: string;
    parent_id: string | null;
    user_id: string | null;
    article_id: string;
  }
  
  interface CommentNode extends BaseComment {
    replies: CommentNode[];
  }
  
  // Format date
  function formatDate(dateString: string) {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
  
  // Format time since
  function formatTimeSince(dateString: string) {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    let interval = Math.floor(seconds / 31536000);
    if (interval >= 1) {
      return `${interval}年前`;
    }
    
    interval = Math.floor(seconds / 2592000);
    if (interval >= 1) {
      return `${interval}ヶ月前`;
    }
    
    interval = Math.floor(seconds / 86400);
    if (interval >= 1) {
      return `${interval}日前`;
    }
    
    interval = Math.floor(seconds / 3600);
    if (interval >= 1) {
      return `${interval}時間前`;
    }
    
    interval = Math.floor(seconds / 60);
    if (interval >= 1) {
      return `${interval}分前`;
    }
    
    return `${Math.floor(seconds)}秒前`;
  }
  
  // コメントを取得する関数
  async function fetchComments() {
    try {
      const { data: fetchedComments, error } = await supabase
        .from('article_comments')
        .select('*')
        .eq('article_id', article.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      console.log('取得したコメント:', fetchedComments); // デバッグ用
      
      // コメントをIDでインデックス化
      const commentMap = new Map<string, CommentNode>();
      
      // 全てのコメントを初期化（逆順で処理）
      (fetchedComments || []).forEach((comment: BaseComment) => {
        commentMap.set(comment.id, {
          ...comment,
          replies: []
        });
      });
      
      // ルートコメントを格納する配列
      const roots: CommentNode[] = [];
      
      // 各コメントを適切な位置に配置（逆順で処理）
      (fetchedComments || []).forEach((comment: BaseComment) => {
        const current = commentMap.get(comment.id);
        if (!current) return;
        
        if (comment.parent_id) {
          // 親コメントを取得
          const parent = commentMap.get(comment.parent_id);
          if (parent) {
            // 親が存在する場合、その親の返信として追加
            parent.replies.unshift(current); // unshiftを使用して先頭に追加
          } else {
            // 親が見つからない場合はルートとして扱う
            roots.unshift(current); // unshiftを使用して先頭に追加
          }
        } else {
          // 親IDがない場合はルートコメント
          roots.unshift(current); // unshiftを使用して先頭に追加
        }
      });
      
      // コメントを更新（ソートは不要になりました）
      comments = roots;
      console.log('最終的なコメントツリー構造:', JSON.stringify(comments, null, 2)); // デバッグ用
    } catch (error) {
      console.error('Error in fetchComments:', error);
    }
  }
  
  // ニックネーム
  let nickname = '';
  
  // Submit a new comment
  async function submitComment() {
    if (!newComment.trim()) {
      errorMessage = 'コメントを入力してください。';
      return;
    }
    
    if (!nickname.trim()) {
      nickname = '名無しさん';
    }
    
    isSubmitting = true;
    errorMessage = '';
    
    try {
      // ユーザーがログインしている場合のみuser_idを設定
      const commentData: any = {
        article_id: article.id,
        content: `${nickname}: ${newComment}`, // ニックネームをコンテンツに含める
        parent_id: null,
        status: 'approved' // 匿名コメントは自動承認
      };
      
      // ログインしている場合のみuser_idを設定
      if ($authStore.user?.id) {
        commentData.user_id = $authStore.user.id;
      }
      
      const { data: comment, error: commentError } = await supabase
        .from('article_comments')
        .insert(commentData)
        .select();
      
      if (commentError) throw commentError;
      
      newComment = '';
      successMessage = 'コメントが投稿されました。';
      
      // コメントを再取得
      await fetchComments();
    } catch (error) {
      console.error('Error submitting comment:', error);
      errorMessage = 'コメントの投稿に失敗しました。後でもう一度お試しください。';
    } finally {
      isSubmitting = false;
    }
  }
  
  // 返信用ニックネーム
  let replyNickname = '';
  
  // Submit a reply to a comment
  async function submitReply() {
    if (!replyContent.trim()) {
      errorMessage = 'コメントを入力してください。';
      return;
    }
    
    if (!replyNickname.trim()) {
      replyNickname = '名無しさん';
    }
    
    isSubmitting = true;
    errorMessage = '';
    
    try {
      // ユーザーがログインしている場合のみuser_idを設定
      const replyData: any = {
        article_id: article.id,
        content: `${replyNickname}: ${replyContent}`, // ニックネームをコンテンツに含める
        parent_id: replyingTo,
        status: 'approved' // 匿名コメントは自動承認
      };
      
      // ログインしている場合のみuser_idを設定
      if ($authStore.user?.id) {
        replyData.user_id = $authStore.user.id;
      }
      
      const { data: reply, error: replyError } = await supabase
        .from('article_comments')
        .insert(replyData)
        .select();
      
      if (replyError) throw replyError;
      
      replyContent = '';
      replyingTo = null;
      successMessage = '返信が投稿されました。';
      
      // コメントを再取得
      await fetchComments();
    } catch (error) {
      console.error('Error submitting reply:', error);
      errorMessage = '返信の投稿に失敗しました。後でもう一度お試しください。';
    } finally {
      isSubmitting = false;
    }
  }
  
  // Cancel replying
  function cancelReply() {
    replyingTo = null;
    replyContent = '';
  }
  
  // 同時接続数データを処理する関数
  function processViewerData(liveStats: Record<string, number> | undefined) {
    if (!liveStats) return { labels: [], data: [] };
    
    // タイムスタンプでソート
    const timestamps = Object.keys(liveStats).sort();
    
    console.log('Raw timestamps:', timestamps); // デバッグ用
    
    // タイムスタンプの形式を確認
    const isNumeric = timestamps.every(ts => !isNaN(Number(ts)));
    
    // 日本時間に変換して表示用のラベルを作成（時間のみ）
    const labels = timestamps.map((ts, index) => {
      let date;
      
      if (isNumeric) {
        // 数値の場合はミリ秒として解釈
        date = new Date(Number(ts));
      } else {
        // 文字列の場合はISO形式として解釈
        date = new Date(ts);
      }
      
      // 無効な日付の場合はインデックスを返す
      if (isNaN(date.getTime())) {
        return `${index + 1}`;
      }
      
      // 時間のみを表示（HH:MM形式）
      return date.toLocaleTimeString('ja-JP', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false // 24時間表示
      });
    });
    
    console.log('Formatted labels:', labels); // デバッグ用
    
    // 視聴者数データ
    const data = timestamps.map(ts => liveStats[ts]);
    
    return { labels, data };
  }
  
  // グラフを初期化する関数
  function initViewerChart() {
    if (!featuredVideos || featuredVideos.length === 0 || !featuredVideos[0].live_stats) return;
    
    const topVideo = featuredVideos[0];
    const { labels, data } = processViewerData(topVideo.live_stats);
    
    if (labels.length === 0 || data.length === 0) return;
    
    // 既存のグラフを破棄
    if (viewerChart) {
      viewerChart.destroy();
    }
    
    // グラフのコンテキストを取得
    const ctx = chartCanvas.getContext('2d');
    if (!ctx) return;
    
    // グラフを作成
    viewerChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: '同時接続数',
          data,
          borderColor: 'rgb(239, 68, 68)', // 赤色
          borderWidth: 2,
          tension: 0.3,
          fill: false,
          pointRadius: 0, // ポイントを非表示
          pointHoverRadius: 0 // ホバー時もポイントを非表示
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              font: {
                size: 12,
                weight: 'bold'
              },
              color: document.documentElement.classList.contains('dark') ? '#ffffff' : '#374151',
              boxWidth: 12,
              padding: 10
            }
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleFont: {
              size: 12,
              weight: 'bold'
            },
            bodyFont: {
              size: 12
            },
            padding: 10,
            cornerRadius: 4,
            displayColors: false,
            callbacks: {
              label: function(context) {
                return `視聴者数: ${context.parsed.y.toLocaleString()} 人`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: {
              color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
            },
            border: {
              display: false
            },
            ticks: {
              color: document.documentElement.classList.contains('dark') ? '#9ca3af' : '#4b5563',
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 10,
              font: {
                size: 10
              }
            }
          },
          y: {
            grid: {
              color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
            },
            border: {
              display: false
            },
            ticks: {
              color: document.documentElement.classList.contains('dark') ? '#9ca3af' : '#4b5563',
              callback: function(value) {
                return value.toLocaleString();
              },
              font: {
                size: 10
              }
            },
            beginAtZero: true
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        },
        animation: {
          duration: 1000,
          easing: 'easeOutQuart'
        }
      }
    });
  }
  
  onMount(() => {
    // Initialize any client-side functionality
    if (typeof window !== 'undefined') {
      const win = window as any;
      if (win.adsbygoogle) {
        (win.adsbygoogle = win.adsbygoogle || []).push({});
      }
      
      // グラフを初期化
      setTimeout(() => {
        initViewerChart();
      }, 500);
    }
  });
</script>

<svelte:head>
  <title>{article.title} | VTGrid</title>
  <meta name="description" content={article.meta_description} />
  {#if article.keywords && article.keywords.length > 0}
    <meta name="keywords" content={article.keywords.join(',')} />
  {/if}
  <meta property="og:title" content={article.title} />
  <meta property="og:description" content={article.meta_description} />
  <meta property="og:url" content={`https://vt-grid.com/daily-articles/${article.slug}`} />
  <meta property="og:image" content={featuredVideos && featuredVideos.length > 0 ? featuredVideos[0].thumbnail_url : 'https://vt-grid.com/og-image.png'} />
  <meta name="twitter:card" content="summary_large_image" />
  <link rel="canonical" href={`https://vt-grid.com/daily-articles/${article.slug}`} />
</svelte:head>

<div class="container mx-auto px-4 py-8">
  <div class="max-w-4xl mx-auto">
    <!-- Breadcrumb -->
    <nav class="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-6">
      <a href="/" class="hover:text-gray-700 dark:hover:text-gray-300">ホーム</a>
      <span class="mx-2">/</span>
      <a href="/daily-articles" class="hover:text-gray-700 dark:hover:text-gray-300">VTuber配信集計ブログ</a>
      <span class="mx-2">/</span>
      <span class="text-gray-700 dark:text-gray-300 truncate">{article.title}</span>
    </nav>
    
    <!-- Article Header -->
    <header class="mb-8">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-4">
        {article.title}
      </h1>
      
      <div class="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
        <div class="flex items-center gap-1.5">
          <Calendar size={16} />
          <time datetime={article.published_at}>{formatDate(article.published_at)}</time>
        </div>
        
        <div class="flex items-center gap-1.5">
          <Eye size={16} />
          <span>{article.view_count.toLocaleString()} 閲覧</span>
        </div>
        
        <div class="flex items-center gap-1.5">
          <MessageCircle size={16} />
          <span>{comments.length} コメント</span>
        </div>
      </div>
    </header>
    
    <!-- Article Content -->
    <div class="prose prose-lg dark:prose-invert max-w-none mb-8">
      {@html article.content}
    </div>
    
    <!-- Viewer Stats Chart -->
    {#if featuredVideos && featuredVideos.length > 0 && featuredVideos[0].live_stats}
      <div class="rounded-xl shadow-lg p-4 md:p-6 mb-12">
        <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-4">視聴者数の推移</h3>
        <p class="text-gray-700 dark:text-gray-300 mb-4 md:mb-6">
          「{featuredVideos[0].title}」の配信における同時接続数の時間ごとの推移です。
        </p>
        
        <div class="h-60 md:h-80 relative rounded-lg">
          <canvas bind:this={chartCanvas}></canvas>
        </div>
        
        <div class="mt-4 text-sm text-gray-700 dark:text-gray-300 text-right">
          <p>最大同時接続数: <span class="font-bold text-red-500">{article.max_concurrent_viewers?.toLocaleString() || '0'}</span> 人</p>
        </div>
      </div>
    {/if}
    
    <!-- Share and Actions -->
    <div class="flex items-center justify-between mb-12 border-t border-b border-gray-200 dark:border-gray-700 py-4">
      <div class="flex items-center gap-4">
        <ShareButton 
          url={`/daily-articles/${article.slug}`}
          title={article.title}
        />
      </div>
      
      <div class="flex items-center gap-2">
        <a href="/daily-articles" class="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
          <ChevronLeft size={16} />
          <span>記事一覧に戻る</span>
        </a>
      </div>
    </div>
    
    <!-- Comments Section -->
    <section id="comments" class="mb-12">
      <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-6">コメント ({comments.length})</h2>
      
      {#if successMessage}
        <div class="bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 p-4 rounded-lg mb-6">
          {successMessage}
        </div>
      {/if}
      
      {#if errorMessage}
        <div class="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 p-4 rounded-lg mb-6">
          {errorMessage}
        </div>
      {/if}
      
      <!-- New Comment Form - ハイエンドな掲示板風デザイン -->
      <div class="mb-8 border-b border-gray-200 dark:border-gray-700 pb-8">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-6">コメントを投稿</h3>
        
        <div class="mb-6">
          <div class="flex flex-col md:flex-row gap-6 mb-4">
            <div class="md:w-1/3">
              <label for="nickname" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">ニックネーム</label>
              <input
                id="nickname"
                type="text"
                bind:value={nickname}
                placeholder="名無しさん"
                class="w-full px-3 py-2 text-gray-700 dark:text-gray-300 border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 bg-transparent"
              />
            </div>
            
            <div class="md:w-2/3">
              <label for="comment" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">コメント</label>
              <textarea
                id="comment"
                bind:value={newComment}
                placeholder="コメントを入力してください..."
                class="w-full px-3 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 bg-transparent"
                rows="4"
              ></textarea>
            </div>
          </div>
          
          <div class="flex justify-between items-center">
            <div class="text-xs text-gray-500 dark:text-gray-400">
              <p>※ニックネームを入力しない場合は「名無しさん」として投稿されます</p>
              <p>※投稿したコメントは削除できません</p>
            </div>
            
            <button
              on:click={submitComment}
              disabled={isSubmitting}
              class="px-6 py-2 bg-transparent border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? '送信中...' : 'コメントを投稿'}
            </button>
          </div>
        </div>
      </div>
      
      <!-- Comments List -->
      {#if comments.length === 0}
        <div class="text-center py-8">
          <p class="text-gray-600 dark:text-gray-400">まだコメントはありません。最初のコメントを投稿しましょう！</p>
        </div>
      {:else}
        <div class="space-y-6">
          {#each comments as comment}
            <div class="border-b border-gray-200 dark:border-gray-700 pb-6 mb-6">
              <!-- Comment Header -->
              <div class="flex items-start gap-3 mb-3">
                
                <div class="flex-1">
                  <div class="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-2">
                    <h4 class="text-sm font-medium text-gray-900 dark:text-white">
                      匿名
                    </h4>
                    <time class="text-xs text-gray-500 dark:text-gray-400" datetime={comment.created_at}>
                      {formatTimeSince(comment.created_at)}
                    </time>
                  </div>
                  
                  <p class="my-3 text-gray-700 dark:text-gray-300 text-sm whitespace-pre-line">
                    {comment.content}
                  </p>
                  
                  <div class="text-right">
                    <button
                      on:click={() => replyingTo = comment.id}
                      class="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      返信
                    </button>
                  </div>
                </div>
              </div>
              
              <!-- Reply Form - ハイエンドなデザイン -->
              {#if replyingTo === comment.id}
                <div class="ml-8 mt-4 border-l-2 border-gray-200 dark:border-gray-700 pl-4">
                  <div class="flex flex-col gap-4 mb-4">
                    <div>
                      <label for="replyNickname" class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">ニックネーム</label>
                      <input
                        id="replyNickname"
                        type="text"
                        bind:value={replyNickname}
                        placeholder="名無しさん"
                        class="w-full px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 bg-transparent"
                      />
                    </div>
                    
                    <div>
                      <label for="replyContent" class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">返信内容</label>
                      <textarea
                        id="replyContent"
                        bind:value={replyContent}
                        placeholder="返信を入力してください..."
                        class="w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 bg-transparent"
                        rows="3"
                      ></textarea>
                    </div>
                  </div>
                  
                  <div class="flex justify-between items-center">
                    <div class="text-xs text-gray-500 dark:text-gray-400">
                      ※名前を入力しない場合は「名無しさん」として投稿
                    </div>
                    
                    <div class="flex gap-2">
                      <button
                        on:click={cancelReply}
                        class="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        キャンセル
                      </button>
                      <button
                        on:click={submitReply}
                        disabled={isSubmitting}
                        class="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isSubmitting ? '送信中...' : '返信を投稿'}
                      </button>
                    </div>
                  </div>
                </div>
              {/if}
              
              <!-- Replies -->
              {#if comment.replies && comment.replies.length > 0}
                <div class="ml-12 mt-4 space-y-4">
                  {#each comment.replies as reply}
                    <div class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                      <div class="flex items-start gap-3">
                        <div class="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex-shrink-0">
                          <div class="w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                        </div>
                        
                        <div class="flex-1">
                          <div class="flex items-center justify-between">
                            <h5 class="text-xs font-medium text-gray-900 dark:text-white">
                              匿名
                            </h5>
                            <time class="text-xs text-gray-500 dark:text-gray-400" datetime={reply.created_at}>
                              {formatTimeSince(reply.created_at)}
                            </time>
                          </div>
                          
                          <p class="mt-1 text-gray-700 dark:text-gray-300 text-xs whitespace-pre-line">
                            {reply.content}
                          </p>
                          
                          <div class="text-right mt-1">
                            <button
                              on:click={() => replyingTo = reply.id}
                              class="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                            >
                              返信
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      <!-- 再帰的に返信を表示 -->
                      {#if reply.replies && reply.replies.length > 0}
                        <div class="ml-4 mt-2 space-y-3">
                          {#each reply.replies as nestedReply}
                            <div class="border-l-2 border-gray-200 dark:border-gray-700 pl-3 py-2">
                              <div class="flex items-start gap-2">
                                <div class="flex-1">
                                  <div class="flex items-center justify-between">
                                    <h5 class="text-xs font-medium text-gray-900 dark:text-white">
                                      匿名
                                    </h5>
                                    <time class="text-xs text-gray-500 dark:text-gray-400" datetime={nestedReply.created_at}>
                                      {formatTimeSince(nestedReply.created_at)}
                                    </time>
                                  </div>
                                  
                                  <p class="mt-1 text-gray-700 dark:text-gray-300 text-xs whitespace-pre-line">
                                    {nestedReply.content}
                                  </p>
                                  
                                  <div class="text-right mt-1">
                                    <button
                                      on:click={() => replyingTo = nestedReply.id}
                                      class="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                                    >
                                      返信
                                    </button>
                                  </div>
                                </div>
                              </div>
                              
                              <!-- Reply Form for Nested Reply -->
                              {#if replyingTo === nestedReply.id}
                                <div class="mt-2 border-l-2 border-gray-200 dark:border-gray-700 pl-3">
                                  <div class="flex flex-col gap-2 mb-2">
                                    <div>
                                      <label for="replyNickname" class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">ニックネーム</label>
                                      <input
                                        id="replyNickname"
                                        type="text"
                                        bind:value={replyNickname}
                                        placeholder="名無しさん"
                                        class="w-full px-2 py-1 text-xs text-gray-700 dark:text-gray-300 border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 bg-transparent"
                                      />
                                    </div>
                                    
                                    <div>
                                      <label for="replyContent" class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">返信内容</label>
                                      <textarea
                                        id="replyContent"
                                        bind:value={replyContent}
                                        placeholder="返信を入力してください..."
                                        class="w-full px-2 py-1 text-xs text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 bg-transparent"
                                        rows="2"
                                      ></textarea>
                                    </div>
                                  </div>
                                  
                                  <div class="flex justify-between items-center">
                                    <div class="text-xs text-gray-500 dark:text-gray-400">
                                      ※名前を入力しない場合は「名無しさん」として投稿
                                    </div>
                                    
                                    <div class="flex gap-2">
                                      <button
                                        on:click={cancelReply}
                                        class="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                      >
                                        キャンセル
                                      </button>
                                      <button
                                        on:click={submitReply}
                                        disabled={isSubmitting}
                                        class="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                      >
                                        {isSubmitting ? '送信中...' : '返信を投稿'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              {/if}
                              
                              <!-- さらに深いレベルの返信を再帰的に表示 -->
                              {#if nestedReply.replies && nestedReply.replies.length > 0}
                                <div class="ml-3 mt-2 space-y-2">
                                  {#each nestedReply.replies as deepReply}
                                    <div class="border-l-2 border-gray-200 dark:border-gray-700 pl-3 py-2">
                                      <div class="flex items-start gap-2">
                                        <div class="flex-1">
                                          <div class="flex items-center justify-between">
                                            <h5 class="text-xs font-medium text-gray-900 dark:text-white">
                                              匿名
                                            </h5>
                                            <time class="text-xs text-gray-500 dark:text-gray-400" datetime={deepReply.created_at}>
                                              {formatTimeSince(deepReply.created_at)}
                                            </time>
                                          </div>
                                          
                                          <p class="mt-1 text-gray-700 dark:text-gray-300 text-xs whitespace-pre-line">
                                            {deepReply.content}
                                          </p>
                                          
                                          <div class="text-right mt-1">
                                            <button
                                              on:click={() => replyingTo = deepReply.id}
                                              class="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                                            >
                                              返信
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      <!-- Reply Form for Deep Reply -->
                                      {#if replyingTo === deepReply.id}
                                        <div class="mt-2 border-l-2 border-gray-200 dark:border-gray-700 pl-2">
                                          <div class="flex flex-col gap-2 mb-2">
                                            <div>
                                              <label for="replyNickname" class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">ニックネーム</label>
                                              <input
                                                id="replyNickname"
                                                type="text"
                                                bind:value={replyNickname}
                                                placeholder="名無しさん"
                                                class="w-full px-2 py-1 text-xs text-gray-700 dark:text-gray-300 border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 bg-transparent"
                                              />
                                            </div>
                                            
                                            <div>
                                              <label for="replyContent" class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">返信内容</label>
                                              <textarea
                                                id="replyContent"
                                                bind:value={replyContent}
                                                placeholder="返信を入力してください..."
                                                class="w-full px-2 py-1 text-xs text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 bg-transparent"
                                                rows="2"
                                              ></textarea>
                                            </div>
                                          </div>
                                          
                                          <div class="flex justify-between items-center">
                                            <div class="text-xs text-gray-500 dark:text-gray-400">
                                              ※名前を入力しない場合は「名無しさん」として投稿
                                            </div>
                                            
                                            <div class="flex gap-2">
                                              <button
                                                on:click={cancelReply}
                                                class="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                              >
                                                キャンセル
                                              </button>
                                              <button
                                                on:click={submitReply}
                                                disabled={isSubmitting}
                                                class="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                              >
                                                {isSubmitting ? '送信中...' : '返信を投稿'}
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      {/if}
                                      
                                      <!-- さらに深いレベルの返信を再帰的に表示（4段階目） -->
                                      {#if deepReply.replies && deepReply.replies.length > 0}
                                        <div class="ml-2 mt-2 space-y-2">
                                          {#each deepReply.replies as veryDeepReply}
                                            <div class="border-l-2 border-gray-200 dark:border-gray-700 pl-2 py-1">
                                              <div class="flex items-start gap-1">
                                                <div class="flex-1">
                                                  <div class="flex items-center justify-between">
                                                    <h5 class="text-xs font-medium text-gray-900 dark:text-white">
                                                      匿名
                                                    </h5>
                                                    <time class="text-xs text-gray-500 dark:text-gray-400" datetime={veryDeepReply.created_at}>
                                                      {formatTimeSince(veryDeepReply.created_at)}
                                                    </time>
                                                  </div>
                                                  
                                                  <p class="mt-1 text-gray-700 dark:text-gray-300 text-xs whitespace-pre-line">
                                                    {veryDeepReply.content}
                                                  </p>
                                                  
                                                  <div class="text-right mt-1">
                                                    <button
                                                      on:click={() => replyingTo = veryDeepReply.id}
                                                      class="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                                                    >
                                                      返信
                                                    </button>
                                                  </div>
                                                </div>
                                              </div>
                                              
                                              <!-- Reply Form for Very Deep Reply -->
                                              {#if replyingTo === veryDeepReply.id}
                                                <div class="mt-1 border-l-2 border-gray-200 dark:border-gray-700 pl-2">
                                                  <div class="flex flex-col gap-1 mb-1">
                                                    <div>
                                                      <label for="replyNickname" class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">ニックネーム</label>
                                                      <input
                                                        id="replyNickname"
                                                        type="text"
                                                        bind:value={replyNickname}
                                                        placeholder="名無しさん"
                                                        class="w-full px-2 py-1 text-xs text-gray-700 dark:text-gray-300 border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 bg-transparent"
                                                      />
                                                    </div>
                                                    
                                                    <div>
                                                      <label for="replyContent" class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">返信内容</label>
                                                      <textarea
                                                        id="replyContent"
                                                        bind:value={replyContent}
                                                        placeholder="返信を入力してください..."
                                                        class="w-full px-2 py-1 text-xs text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 bg-transparent"
                                                        rows="2"
                                                      ></textarea>
                                                    </div>
                                                  </div>
                                                  
                                                  <div class="flex justify-between items-center">
                                                    <div class="text-xs text-gray-500 dark:text-gray-400">
                                                      ※名前を入力しない場合は「名無しさん」として投稿
                                                    </div>
                                                    
                                                    <div class="flex gap-2">
                                                      <button
                                                        on:click={cancelReply}
                                                        class="px-1 py-0.5 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                                      >
                                                        キャンセル
                                                      </button>
                                                      <button
                                                        on:click={submitReply}
                                                        disabled={isSubmitting}
                                                        class="px-1 py-0.5 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                                      >
                                                        {isSubmitting ? '送信中...' : '返信を投稿'}
                                                      </button>
                                                    </div>
                                                  </div>
                                                </div>
                                              {/if}
                                            </div>
                                          {/each}
                                        </div>
                                      {/if}
                                    </div>
                                  {/each}
                                </div>
                              {/if}
                              
                              <!-- Reply to Reply Form -->
                              {#if replyingTo === reply.id}
                                <div class="mt-2 border-l-2 border-gray-200 dark:border-gray-700 pl-4">
                                  <div class="flex flex-col gap-3 mb-3">
                                    <div>
                                      <label for="replyNickname" class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">ニックネーム</label>
                                      <input
                                        id="replyNickname"
                                        type="text"
                                        bind:value={replyNickname}
                                        placeholder="名無しさん"
                                        class="w-full px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 bg-transparent"
                                      />
                                    </div>
                                    
                                    <div>
                                      <label for="replyContent" class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">返信内容</label>
                                      <textarea
                                        id="replyContent"
                                        bind:value={replyContent}
                                        placeholder="返信を入力してください..."
                                        class="w-full px-3 py-2 text-xs text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 bg-transparent"
                                        rows="2"
                                      ></textarea>
                                    </div>
                                  </div>
                                  
                                  <div class="flex justify-between items-center">
                                    <div class="text-xs text-gray-500 dark:text-gray-400">
                                      ※名前を入力しない場合は「名無しさん」として投稿
                                    </div>
                                    
                                    <div class="flex gap-2">
                                      <button
                                        on:click={cancelReply}
                                        class="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                      >
                                        キャンセル
                                      </button>
                                      <button
                                        on:click={submitReply}
                                        disabled={isSubmitting}
                                        class="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                      >
                                        {isSubmitting ? '送信中...' : '返信を投稿'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              {/if}
                            </div>
                          {/each}
                        </div>
                      {/if}
                    </div>
                  {/each}
                </div>
              {/if}
            </div>
          {/each}
        </div>
      {/if}
    </section>
    
    <!-- Ad Space -->
    <div class="mt-12 mb-8">
      <ins class="adsbygoogle"
           style="display:block"
           data-ad-client="ca-pub-1653216533599284"
           data-ad-slot="8708608575"
           data-ad-format="auto"
           data-full-width-responsive="true"></ins>
    </div>
  </div>
</div>

<style>
  .aspect-w-16 {
    position: relative;
    padding-bottom: 56.25%;
  }
  
  .aspect-w-16 > img {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    object-fit: cover;
  }
  
  /* Prose styles for article content */
  :global(.prose) {
    @apply text-gray-700 dark:text-gray-300;
  }
  
  :global(.prose h2) {
    @apply text-2xl font-bold text-gray-900 dark:text-white mt-8 mb-4;
  }
  
  :global(.prose h3) {
    @apply text-xl font-bold text-gray-900 dark:text-white mt-6 mb-3;
  }
  
  :global(.prose p) {
    @apply mb-4;
  }
  
  :global(.prose a) {
    @apply text-blue-600 dark:text-blue-400 hover:underline;
  }
  
  :global(.prose ul) {
    @apply list-disc pl-6 mb-4;
  }
  
  :global(.prose ol) {
    @apply list-decimal pl-6 mb-4;
  }
  
  :global(.prose img) {
    @apply rounded-lg my-6 mx-auto;
  }
  
  :global(.prose blockquote) {
    @apply border-l-4 border-gray-300 dark:border-gray-700 pl-4 italic my-6;
  }
</style>
